import { Injectable } from '@angular/core';
import { Logger } from '../../core/services/logger.service';
import { HttpClient } from '@angular/common/http';
import { Observable, map, throwError, Subject, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface Notification {
  id: string;
  message: string;
  type: string; // 'message' ou autres types futurs
  readAt: Date | null;
  createdAt: Date;
  redirectUrl: string;
  contextData: Record<string, any>;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly logger = new Logger(NotificationService.name);
  private apiUrl = `${environment.apiUrl}/api/notifications`;

  // Subject pour émettre des événements lorsqu'une notification est marquée comme lue
  private notificationReadSubject = new Subject<void>();

  // Observable public que les composants peuvent écouter
  public notificationRead$ = this.notificationReadSubject.asObservable();

  constructor(private http: HttpClient) {}

  getUnreadNotifications(): Observable<Notification[]> {
    return this.http
      .get<{ data: Notification[] }>(`${this.apiUrl}/unread`)
      .pipe(map(response => response.data));
  }

  getReadNotifications(): Observable<Notification[]> {
    return this.http
      .get<{ data: Notification[] }>(`${this.apiUrl}/read`)
      .pipe(map(response => response.data));
  }

  getNotifications(): Observable<Notification[]> {
    // Combine unread and read notifications
    return this.getUnreadNotifications();
  }

  getUnreadCount(): Observable<number> {
    return this.http
      .get<{ count: number }>(`${this.apiUrl}/unread/count`)
      .pipe(map(response => response.count));
  }

  markAsRead(notificationId: string): Observable<void> {
    // Vérifier que l'ID est valide avant d'envoyer la requête
    if (!notificationId || notificationId === 'undefined') {
      this.logger.error('ID de notification invalide', notificationId);
      return throwError(() => new Error('ID de notification invalide'));
    }

    return this.http
      .patch<void>(`${this.apiUrl}/${notificationId}/read`, {})
      .pipe(
        tap(() => {
          // Émettre un événement pour informer les composants qu'une notification a été lue
          this.notificationReadSubject.next();
        })
      );
  }

  markAllAsRead(): Observable<{ message: string; count: number }> {
    return this.http
      .patch<{
        message: string;
        count: number;
      }>(`${this.apiUrl}/mark-all-read`, {})
      .pipe(
        tap(() => {
          // Émettre un événement pour informer les composants que toutes les notifications ont été lues
          this.notificationReadSubject.next();
        })
      );
  }
}
